import logoBT from "@assets/logo-masthead.svg";
import logoUOB from "@assets/logo-uob-blue.png";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import MetaTags from "@components/MetaTags/MetaTags";
import EventsAwardsHeader from "@events-awards/EventAwardsHeader";
import EventsAwardsNavigationBar from "@events-awards/EventsAwardsNavigationBarComponents/EventAwardsNavigationBar";
import SIAFooter from "@events-awards/SIA/components/SIAFooter";
import {
  EVENTS_AWARDS_SIA_LABEL,
  EVENTS_AWARDS_SIA_PATH,
} from "@events-awards/utils/constants";
import {
  AwardType,
  EventsAwardsHomePageContextData,
} from "@events-awards/utils/types";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { useDetectAdBlock } from "adblock-detect-react";

/**
 * Component to render event and awards page
 */
const EventsAwardsSIAPageContent = ({
  articleProps,
}: EventsAwardsHomePageContextData): React.ReactElement => {
  const adBlockDetected = useDetectAdBlock();

  const variant: CardVariations = {
    title: {
      color: "text-gray-850",
      size: "text-6xl",
      extraClass: "font-public-sans font-semibold pt-4 lg:pt-0",
    },
    kicker: {
      color: "text-gray-515",
      extraClass: "pt-4 lg:pt-0 font-public-sans",
    },
    image: {
      width: "w-full lg:w-1/2",
      position: "left",
      aspectRatio: "object-cover",
    },
    content: {
      width: "w-full lg:w-1/2",
      extraClass: "px-3 text-left lg:px-14",
      blurb: {
        extraClass: "text-gray-750 font-sans",
      },
    },
  };

  return (
    <div
      className="w-full"
      data-testid="events-awards-sia-page-content-component"
    >
      <AdSettings
        adNames={["lb1", "prestitial", "catfish"]}
        path={EVENTS_AWARDS_SIA_PATH}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData
        title="Sustainability Impact Awards"
        level2="events awards"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <MetaTags
        title="Sustainability Impact Awards Latest News & Headlines - THE BUSINESS TIMES"
        description={`THE BUSINESS TIMES ${EVENTS_AWARDS_SIA_LABEL} - Find ${EVENTS_AWARDS_SIA_LABEL} News & Headlines, insight and analysis in Singapore, Asia-Pacific & global markets news at The Business Times.`}
        ogType="website"
        slug={EVENTS_AWARDS_SIA_PATH}
      />

      <EventsAwardsHeader />

      <Container>
        <Column rootClassName="w-full text-center p-0">
          <EventsAwardsNavigationBar awardType={AwardType.SIA} />
        </Column>
      </Container>

      {articleProps ? (
        <Row rootClassName="justify-center bg-gray-150 mb-8">
          <Container>
            <Column rootClassName="w-full text-center lg:w-10/12 lg:mx-auto">
              <Row rootClassName="mb-5">
                <Card
                  {...articleProps}
                  className="mx-0 mt-4 justify-center"
                  variations={variant}
                />
              </Row>
            </Column>
          </Container>
        </Row>
      ) : null}

      <Container>
        <Row rootClassName="justify-center">
          <Column rootClassName="w-full text-center lg:w-10/12">
            <Row rootClassName="flex mb-9 text-gray-850">
              <Column rootClassName="w-full lg:w-4/12 lg:pr-7">
                <h2 className="leading-1 mb-2 text-left font-poppins text-4xl font-medium md:mb-6 lg:mb-0 lg:text-8xl">
                  {"Sustainability "}
                  <br className="hidden lg:inline" />
                  {"Impact Awards"}
                </h2>
              </Column>

              <Column rootClassName="w-full text-left lg:w-8/12 lg:pl-7">
                <p className="text-md font-public-sans leading-7">
                  {
                    "The Sustainability Impact Awards (SI Awards), launched on 1 February 2023, is jointly presented by The Business Times and UOB, with the Centre for Governance and Sustainability (CGS) at the National University of Singapore Business School as the knowledge partner."
                  }
                </p>
              </Column>
            </Row>
          </Column>
        </Row>
      </Container>

      <Row rootClassName="flex py-9 px-3 justify-center items-center bg-gray-150 mt-1 md:px-6 lg:px-0 ">
        <Container>
          <Column rootClassName="w-full text-center items-center justify-center flex flex-col md:flex-row md:px-0">
            <IconFactory type={IconTypeEnum.apply} x={80} y={60} size={80} />

            <h2 className="text-center align-middle font-poppins text-4xl font-medium text-gray-850 md:text-left md:text-6xl lg:text-8xl">
              Nominations for SI Awards 2025 are closed
            </h2>
          </Column>
        </Container>
      </Row>

      <Container>
        <Row rootClassName="justify-center">
          <Column rootClassName="w-full text-center mb-8">
            <Row rootClassName="py-9 px-3 justify-center md:px-6 lg:px-0">
              <Column rootClassName="text-left lg:pl-7 justify-center">
                <BlockTitle
                  text="Jointly organised by"
                  rootClassName="text-center mb-2"
                />

                <Row rootClassName="justify-center items-center">
                  <Column>
                    <img
                      src={logoBT}
                      width={211}
                      alt="The Business Times logo"
                      className="my-6 md:mx-16"
                    />
                  </Column>

                  <Column>
                    <img
                      src={logoUOB}
                      width={141}
                      alt="UOB logo"
                      className="mx-16 my-6"
                    />
                  </Column>
                </Row>
              </Column>
            </Row>

            <Row rootClassName="text-center py-3 text-gray-850 font-public-sans">
              <Column rootClassName="w-full py-3 lg:w-1/3 lg:py-0">
                <p>
                  <strong>Dr Amy Khor</strong>
                  <br />
                  Chairman of the Judging Panel
                </p>
              </Column>

              <Column rootClassName="w-full py-3 lg:w-1/3 lg:py-0">
                <p>
                  <strong>Ms Chen Huifen</strong>
                  <br />
                  Editor
                  <br />
                  The Business Times
                </p>
              </Column>

              <Column rootClassName="w-full py-3 pb-0 lg:w-1/3 lg:py-0">
                <p>
                  <strong>Mr Geoffrey Yeo</strong>
                  <br />
                  Assistant Managing Director, Capabilities, Urban
                  <br />
                  Systems &amp; Solutions, Enterprise Singapore
                </p>
              </Column>
            </Row>

            <Row rootClassName="text-center py-3 text-gray-850">
              <Column rootClassName="w-full py-3 lg:w-1/3 lg:py-0">
                <p>
                  <strong>Professor Lawrence Loh</strong>
                  <br />
                  Director, Centre for Governance and Sustainability
                  <br />
                  Professor, Strategy and Policy
                  <br />
                  NUS Business School
                  <br />
                  National University of Singapore
                </p>
              </Column>

              <Column rootClassName="w-full py-3 lg:w-1/3 lg:py-0">
                <p>
                  <strong>Ms Eliza Tan</strong>
                  <br />
                  Senior Vice President
                  <br />
                  Head, IPO Admissions
                  <br />
                  Head, Sustainable Development Office
                </p>
              </Column>

              <Column rootClassName="w-full py-3 pb-0 lg:w-1/3 lg:py-0">
                <p>
                  <strong>Mr Tan Swee Yiow</strong>
                  <br />
                  President of REDAS
                  <br />
                  Real Estate Developers&apos;s Association of Singapore
                  <br />
                  (REDAS)
                </p>
              </Column>
            </Row>

            <Row rootClassName="text-center justify-center py-3 text-gray-850">
              <Column rootClassName="w-full lg:w-1/3">
                <p>
                  <strong>Ms Theresa Goh</strong>
                  <br />
                  Managing Founder
                  <br />
                  360 Dynamics Limited
                </p>
              </Column>
            </Row>
          </Column>
        </Row>
      </Container>

      <SIAFooter />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsSIAPageContent;
